import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const Namuna8SanyuktArjForm = () => {
  const componentRef = useRef();
  const { language } = useLanguage();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: 'Namuna8_Sanyukt_Arj.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'प्रिंट करा' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'पीडीएफ डाउनलोड करा' : 'Download PDF'}
        </button>
      </div>

      {/* A4 Container */}
      <div className="w-full overflow-x-auto flex justify-center">
        <div ref={componentRef} className="bg-white text-black shadow-lg print:shadow-none relative" style={{ width: '210mm' }}>
          
          <div className="px-10 py-10 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="text-center mb-1">
              <p className="font-bold">{language === 'marathi' ? '"महापंचायतराज अभियान"' : '"MahaPanchayatRaj Abhiyan"'}</p>
            </div>

            <div className="text-center mb-6">
              <h1 className="text-lg border-b border-black inline-block pb-0.5">
                {language === 'marathi' ? 'ग्रामपंचायत नमुना नं.८ वरील नोंदी पती- पत्नी यांच्या संयुक्त नावे करण्यासाठी अर्ज' : 'Application for making entries on Gram Panchayat Form No. 8 in joint names of husband and wife'}
              </h1>
            </div>

            <div className="flex justify-between items-end mb-4 text-[15px]">
              <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
              <p className="flex items-end">
                <span className="mr-1">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                <span className="border-b border-dashed border-black w-8 inline-block"></span>
                <span className="mx-1">/</span>
                <span className="border-b border-dashed border-black w-8 inline-block"></span>
                <span className="mx-1">/२०</span>
              </p>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
              <p className="font-bold mb-1">{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी,' : 'Sarpanch / Gram Panchayat Officer,'}</p>
              
              <p className="flex items-end mb-1">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
              </p>
              <p className="flex items-end">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
                <span className="border-b border-dashed border-black w-40"></span>
                <span className="shrink-0 mx-2">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
                <span className="border-b border-dashed border-black w-40"></span>
              </p>
            </div>

            <div className="pl-12 mb-4 text-[15px] leading-relaxed">
               <p className="font-bold mb-2">{language === 'marathi' ? 'विषय :- ग्रामपंचायत नमुना नं.८ वरील नोंदी पती- पत्नी यांच्या संयुक्त नावे करणेबाबत.' : 'Subject :- Regarding making entries on Gram Panchayat Form No. 8 in joint names of husband and wife.'}</p>
               <div className="flex flex-col mb-4">
                 <div className="flex">
                   <span className="shrink-0 mr-2">{language === 'marathi' ? 'संदर्भ:' : 'Ref:'}</span>
                   <p>{language === 'marathi' ? '१)' : '1)'} {language === 'marathi' ? 'ग्रामविकास विभाग ' : 'Rural Development Dept '}<span className="text-blue-700">{language === 'marathi' ? 'शासन परिपत्रक दि. २० नोव्हेंबर २००३' : 'Govt Circular dt 20 Nov 2003'}</span></p>
                 </div>
                 <div className="flex pl-10 mt-1">
                   <p>{language === 'marathi' ? '२) ग्रामविकास विभाग शासन परिपत्रक दि. २ मे २०२२' : '2) Rural Development Dept Govt Circular dt 2 May 2022'}</p>
                 </div>
               </div>
               
               <p className="flex items-end mb-2">
                 <span className="shrink-0 font-bold mr-2">{language === 'marathi' ? 'अर्जदार :-' : 'Applicant :-'}</span>
                 <span className="shrink-0 mr-1">{language === 'marathi' ? 'श्री. / सौ.' : 'Shri. / Smt.'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end pl-16">
                 <span className="shrink-0 mr-2">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="shrink-0 mx-2">{language === 'marathi' ? 'मो.नं.' : 'Mob.No.'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
            </div>

            <div className="mb-6 text-[15px] leading-relaxed">
              <p className="mb-3">{language === 'marathi' ? 'मा. महोदय,' : 'Respected Sir/Madam,'}</p>
              <div className="pl-12 flex flex-wrap leading-loose items-end">
                 <span className="mr-1 mt-1">{language === 'marathi' ? 'वरील विषयान्वये सविनय अर्ज सादर करतो / करते की, ग्रामपंचायत हद्दीतील मौजे' : 'With reference to the above subject, I humbly submit the application that, within the Gram Panchayat limits at Mouje'}</span>
                 <span className="border-b border-dashed border-black flex-grow mt-1 inline-block"></span>
              </div>
              <p className="mt-1 leading-loose text-justify">
                {language === 'marathi' ? 'येथील खालील मिळकत/मिळकती माझ्या नावे आहे. संदर्भीय शासन परिपत्रक नुसार माझ्या नावावरील मिळकती माझ्या व माझ्या पत्नीच्या संयुक्त नावे करण्यासाठी दोघांच्या संमतीनुसार अर्ज सादर करित आहे.' : 'the following property/properties are in my name. As per the reference government circular, I am submitting the application with mutual consent to transfer the properties in my name to the joint names of myself and my wife.'}
              </p>
            </div>

            <div className="mb-6 w-full text-[15px]">
              <table className="w-full border-collapse border border-black">
                <thead>
                  <tr className="bg-white">
                    <th className="border border-black px-2 py-2 text-left font-normal w-12">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No'}</th>
                    <th className="border border-black px-2 py-2 text-left font-normal w-32">{language === 'marathi' ? 'गावाचे नाव' : 'Village Name'}</th>
                    <th className="border border-black px-2 py-2 text-left font-normal w-24">{language === 'marathi' ? 'मिळकत क्रमांक' : 'Property Number'}</th>
                    <th className="border border-black px-2 py-2 text-left font-normal">{language === 'marathi' ? 'मालकाचे नाव' : 'Owner\'s Name'}</th>
                    <th className="border border-black px-2 py-2 text-left font-normal w-56">{language === 'marathi' ? 'न.नं. ८ मध्ये दाखल करावयाचे पत्नीचे नाव' : 'Wife\'s name to be entered in Form No. 8'}</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map((index) => (
                    <tr key={index} className="h-10">
                      <td className="border border-black"></td>
                      <td className="border border-black"></td>
                      <td className="border border-black"></td>
                      <td className="border border-black"></td>
                      <td className="border border-black"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mb-8 text-[15px] leading-loose">
              <p className="pl-12">
                {language === 'marathi' ? 'वरीलप्रमाणे माहिती बरोबर असून आमच्या वरील नमूद मिळकती पती-पत्नी संयुक्त नावे करण्यात याव्यात, ही नम्र विनंती.' : 'The above information is correct and it is a humble request that our above mentioned properties should be put in the joint names of husband and wife.'}
              </p>
            </div>

            <div className="flex justify-between items-start text-[15px] mt-auto w-full">
               <div className="w-1/2 pr-4 space-y-3">
                  <p className="font-bold mb-4">{language === 'marathi' ? 'सोबत :' : 'Enclosures :'}</p>
                  <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? '१)' : '1)'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? '२)' : '2)'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
               </div>

               <div className="w-1/2 flex flex-col space-y-3 pl-8">
                  <p className="flex items-end">
                    <span className="shrink-0 font-bold mr-2 w-20">{language === 'marathi' ? 'पतीचे नाव' : 'Husband\'s Name'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="shrink-0 mr-2 w-20 text-right pr-2">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="shrink-0 font-bold mr-2 w-20">{language === 'marathi' ? 'पत्नीचे नाव' : 'Wife\'s Name'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="shrink-0 mr-2 w-20 text-right pr-2">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
               </div>
            </div>

            <div className="absolute bottom-6 left-10 text-blue-700 text-sm underline print:hidden">
              www.majhigrampanchayat.com
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Namuna8SanyuktArjForm;
